import { createContext, ReactNode, useContext } from "react";
import { useOptroBulkImportEnabled } from "../hooks/useOptroBulkImportEnabled";
import { useOptroImportAvailable } from "../hooks/useOptroImportAvailable";
import {
  OptroImportTracker,
  useOptroImportJobs,
} from "../hooks/useOptroImportJobs";
import {
  OptroControlSummary,
  OptroControlSummarySchema,
  OptroImportStartItem,
  workpaperFileToChoice,
} from "../models/audit/optroSync";
import { hasPermission } from "../lib/permissions";
import { useAuth } from "./authContext";

export type OptroImportContextValue = OptroImportTracker<
  OptroImportStartItem,
  OptroControlSummary
>;

const OptroImportContext = createContext<OptroImportContextValue | null>(null);

// Prefer the tenant's friendly control code (e.g. "US.CR.1.C05") over the
// compound external ref, matching the select/review tables. This label also
// rides the import request as `controlName`, so the progress panel and the
// backend's status metadata show the same friendly name.
function controlNameFor(item: OptroImportStartItem): string {
  return `${item.control.idCode ?? item.control.externalRef} - ${item.control.name}`;
}

export function OptroImportProvider({ children }: { children: ReactNode }) {
  const { workspaceMember } = useAuth();
  const bulkImportEnabled = useOptroBulkImportEnabled();
  const auditAvailable = useOptroImportAvailable();
  const tracker = useOptroImportJobs<OptroImportStartItem, OptroControlSummary>(
    {
      kind: "control",
      // The controls listing needs the bulk-import flag and edit_control, and
      // the user must be able to reach the audit surface at all.
      enabled:
        auditAvailable &&
        bulkImportEnabled &&
        hasPermission(workspaceMember, "edit_control"),
      basePath: "/controls/external",
      noun: "controls",
      toRequestItem: (item) => ({
        externalRef: item.control.externalRef,
        controlName: controlNameFor(item),
        workpapers: item.workpapers.map(workpaperFileToChoice),
        // Description blanked: it can be long, and placeholders don't show it.
        display: { ...item.control, description: null },
      }),
      toOptimisticItem: (item) => ({
        controlExternalRef: item.control.externalRef,
        controlName: controlNameFor(item),
        status: "queued",
      }),
      summaryOf: (item) => item.control,
      summarySchema: OptroControlSummarySchema,
    },
  );
  return (
    <OptroImportContext.Provider value={tracker}>
      {children}
    </OptroImportContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptroImport(): OptroImportContextValue {
  const value = useContext(OptroImportContext);
  if (!value) {
    throw new Error("useOptroImport must be used within OptroImportProvider");
  }
  return value;
}

// eslint-disable-next-line react-refresh/only-export-components -- For surfaces that can render outside the provider (the sidebar's Background Tasks), which then show no imports.
export function useOptroImportIfAvailable(): OptroImportContextValue | null {
  return useContext(OptroImportContext);
}
